import { groupBy, maxBy, sortBy, sumBy } from 'lodash';
import type { MathExpr } from '../../lib/math';
import { mathDiv, mathMul, mathSub, mathVar } from '../../lib/math';
import type { Distances } from '../distances/context';
import type { BestSchedule } from './suggestions/Suggestions';
import type { ScheduleDataContext } from './ScheduleDataContext';
import type { TimeDataTotals, TimeData, Events } from './TimeData';
import {
  getEventStartLocationId,
  getEventEndLocationId,
  isPortCallDepartureEventTimeData,
  isLoadEventTimeData,
  isCargoEventTimeData,
  isCargoEvent,
  isTCOrderEventTimeData,
  isTimeData,
} from './TimeData';
import type { ScenarioOrder } from '../../lib/planningData';
import { memoize2 } from '@seaber/common/lib/memoize';
import type { GetIncomeForLoadedAmount } from '../shipment/income/income';
import { isEuEtsCostEnabledForUser } from '../shipment/income/income';
import type { TceComputationOptions } from '../shipment/TCE';
import { getCostSharingDetailsForPortCalls } from '../shipment/TCE';
import { getEventShipmentId } from '../shipment/shipment';
import { computeTceForTimeData } from '../shipment/TCE';
import { parseClientId, type ClientId } from '@seaber/common/lib/clientId';
import type { PnlData } from '../hooks/usePnlData';
import { DefaultMap } from '@seaber/common/lib/collections';
import { mapFind, rangesOverlap } from '../../lib/util';
import type { CalculateTimeDataPnl } from '../pnl/ui/context';
import type { PnlProcessorContextInput } from '../pnl/context/context';
import { getEventsFinancialBounds } from '../shipment/timelineBounds';
import type { Vessel } from './Vessel';
import type { EventTimeline, VoyageGroup } from './Timelines';
import {
  generatePortCallsLegsAndWaits,
  getOrdersFromTimeDatas,
} from './Timelines';
import { getOverlappingEvents } from './util';
import { excludeDryDockPeriods } from './TimeData';
import {
  assignNonOverlappingEventsToNextShipment,
  freeEventFilter,
  isLegOrWait,
} from '../timeline/utils/utils';
import { computePnlPortCostByPortCall } from '../shipment/costs/costs';
import type { PortCostPriority } from '../shipment/costs/costs';
import type { MarcuraPortCosts } from '../marcura/context';
import type { GlobalStore } from '../../state/zustand';
import type { Currency } from '../../graphql';

const getOrderShortestDistance = memoize2(
  (
    order: Pick<ScenarioOrder, 'loadPortId' | 'dischargePortId'>,
    distances: Distances,
  ): number | null => {
    if (!order.loadPortId) {
      return null;
    }
    return (
      distances.get(order.loadPortId, order.dischargePortId, false)?.dist ??
      null
    );
  },
);

/** If an order would have 0 mass, uses 1 instead */
export function countTotalMassDistance(
  timedata: TimeData[],
  distances: Distances,
  /** When normalized, result is divided by total mass. This is useful when evaluating partial solutions while constructing suggestions. */
  normalize?: boolean,
): number {
  let totalMass = 0;
  let massDistance = 0;

  for (const item of timedata) {
    if (isLoadEventTimeData(item)) {
      const distance = getOrderShortestDistance(item.event.order, distances);

      if (distance) {
        // 0 mass is invalid data but happens sometimes. 1 fallback makes the cargo undesirable but still comparable against others without specified mass
        const mass = item.mass || 1;
        totalMass += mass;
        massDistance += mass * distance;
      }
    }
  }

  return normalize && totalMass !== 0 ? massDistance / totalMass : massDistance;
}

/** Calculates DTM (Demand Ton-Miles): Σ w_o · d_min,o - using shortest routed distances */
export function calculateDTM(
  timedata: TimeData[],
  distances: Distances,
): number {
  return countTotalMassDistance(timedata, distances);
}

/** cost / demand ton-miles. */
export function calculateCostPerTonMile(
  cost: number,
  massDistance: number,
): number {
  // massDistance can be 0 when a distance is missing, but never with valid data
  return cost / Math.max(1, massDistance);
}

/** Calculates CTM (Carried Ton-Miles): Σ w_o · d_o - using actual voyage leg distances */
export function calculateCTM(schedule: TimeData[]): number {
  return sumBy(
    schedule,
    (td) =>
      // used capacity is AFTER the event, to get the mass before we subtract the change
      (td.usedMassCapacity - td.mass) * td.distance,
  );
}

/** Calculates ATM (Available Ton-Miles): DWT × Σ d_k */
export function calculateATM(schedule: TimeData[], dwt: number): number {
  const totalDistance = countTotalDistance(schedule);
  return dwt * totalDistance;
}

/** Given a continous sequence of tds, gives the total distance as a sum of all legs between tds */
export function countTotalDistance(tds: TimeData[]): number {
  return sumBy(tds, (td) => td.distance);
}

export function countVoyageCargoPerformance(
  schedule: { totals: TimeDataTotals; timeData: TimeData[] },
  originalMassDistance: number,
  originalDistance: number,
  distances: Distances,
  /** When normalized, total mass distance is divided by total mass. This is useful when evaluating partial solutions while constructing suggestions. */
  normalize?: boolean,
) {
  const totalDistance = schedule.totals.distance;
  const totalMassDistance = countTotalMassDistance(
    schedule.timeData,
    distances,
    normalize,
  );
  return (
    (totalMassDistance - originalMassDistance) /
    Math.max(1, totalDistance - originalDistance)
  );
}

/** Returns the last shipment in the schedule which is within the range of the given orders' events */
function getLastOverlappingShipmentForOrders(
  events: Events[],
  orders: Set<number>,
): ClientId | null {
  const shipmentRanges = new DefaultMap<
    ClientId,
    { start: number; end: number }
  >(() => ({ start: Infinity, end: -Infinity }));
  const newOrderRanges = new DefaultMap<number, { start: number; end: number }>(
    () => ({ start: Infinity, end: -Infinity }),
  );

  events.forEach((event, i) => {
    if (isCargoEvent(event)) {
      if (orders.has(event.order.id)) {
        newOrderRanges.update(event.order.id, (x) => ({
          start: Math.min(x?.start ?? i, i),
          end: Math.max(x?.end ?? i, i),
        }));
      } else {
        const shipmentId = getEventShipmentId(event);
        if (!shipmentId) return;
        shipmentRanges.update(shipmentId, (x) => ({
          start: Math.min(x?.start ?? i, i),
          end: Math.max(x?.end ?? i, i),
        }));
      }
    }
  });

  const overlappingShipments = [...shipmentRanges].filter(
    ([, shipmentRange]) => {
      return !!mapFind(newOrderRanges, (orderRange) =>
        rangesOverlap(shipmentRange, orderRange),
      );
    },
  );

  const lastStartedShipment = maxBy(
    overlappingShipments,
    ([, shipmentRange]) => shipmentRange.start,
  );

  return lastStartedShipment?.[0] ?? null;
}

/** Given a suggestion, returns which shipment the suggestion should be added to.
 * This is based on the last shipment in the original schedule which is overlapped by the new suggestion. */
export function getTargetShipmentForSuggestion(
  combination: BestSchedule['combination'],
): ClientId | null {
  return getLastOverlappingShipmentForOrders(
    combination.schedule,
    new Set(combination.assignments.map((a) => a.order.id)),
  );
}

/** Given a range of tds, expand it to include all tds in the first and last port calls.
 * e.g. timeData = [Arrival, Berthing, Load0, Departure, Arrival, Berthing, Discharge0, Load1, Departure, Arrival, Berthing, Discharge1, Departure],
 *      startIndex = 7 (Load1), endIndex = 11 (Disharge1)
 *   => [4, 11] (includes the Arrival, Berthing, Discharge0 before Load1, and Departure after Discharge1)
 *
 */
export function expandTdRangeToFullPortCalls(
  timeData: TimeData[],
  startIndex: number,
  endIndex: number,
): [number, number] {
  while (
    startIndex > 0 &&
    getEventStartLocationId(timeData[startIndex].event) ===
      getEventEndLocationId(timeData[startIndex].event) &&
    getEventStartLocationId(timeData[startIndex].event) ===
      getEventStartLocationId(timeData[startIndex - 1].event)
  ) {
    startIndex--;
  }
  if (isPortCallDepartureEventTimeData(timeData[startIndex])) {
    startIndex++;
  }
  while (
    endIndex < timeData.length - 1 &&
    getEventStartLocationId(timeData[startIndex].event) ===
      getEventEndLocationId(timeData[startIndex].event) &&
    getEventEndLocationId(timeData[endIndex].event) ===
      getEventEndLocationId(timeData[endIndex + 1].event)
  ) {
    endIndex++;
  }
  return [startIndex, endIndex];
}

export const findShipmentRange = (
  timeData: TimeData[],
  shipmentId: ClientId,
): { start: number; end: number } => {
  return {
    start: timeData.findIndex(
      (td) =>
        isCargoEventTimeData(td) && getEventShipmentId(td.event) === shipmentId,
    ),
    end: timeData.findLastIndex(
      (td) =>
        isCargoEventTimeData(td) && getEventShipmentId(td.event) === shipmentId,
    ),
  };
};

/** Updates the shipmentId on the entries for the given orders. Returns a new array.
 * Used to manipulate the entries from suggestions after we have identified the final shipment to attach to or create. */
export const patchOrderShipmentIds = (
  timeData: readonly TimeData[],
  orderIds: Set<number>,
  newShipmentId: ClientId,
): TimeData[] => {
  return timeData.map((td) => {
    if (isTCOrderEventTimeData(td) && orderIds.has(td.event.order.id)) {
      return {
        ...td,
        event: {
          ...td.event,
          entry: {
            ...td.event.entry,
            shipmentId: newShipmentId,
          },
        },
      };
    }
    return td;
  });
};

export const NEW_SUGGESTION_SHIPMENT_ID = parseClientId(
  'NEW_SUGGESTION_SHIPMENT_ID',
);

export function calculateTceRange(
  schedule: NonNullable<BestSchedule>,
  targetShipmentId: ClientId | null,
  range: 'overlapping_indices' | 'overlapping_voyage',
): {
  timeData: TimeData[];
  startIndex: number;
  endIndex: number;
  targetShipmentId: ClientId;
} {
  const assignments = schedule.combination.assignments;
  let { timeData } = schedule;

  const lastIndex = timeData.length - 1;
  /** First Td index included in TCE calculation */
  let startIndex = lastIndex;
  /** Last Td index included in TCE calculation */
  let endIndex = 0;

  if (range === 'overlapping_voyage') {
    targetShipmentId ??= getTargetShipmentForSuggestion(schedule.combination);

    if (targetShipmentId) {
      // Initially set shipment bounds to existing shipment tds
      ({ start: startIndex, end: endIndex } = findShipmentRange(
        timeData,
        targetShipmentId,
      ));
    }
  }

  targetShipmentId ??= NEW_SUGGESTION_SHIPMENT_ID;

  // Until now it is still possible that new suggestion entries do not have a shipmentId. The rest of the code requires a shipmentId.
  timeData = patchOrderShipmentIds(
    timeData,
    new Set(assignments.map((a) => a.order.id)),
    targetShipmentId,
  );

  // Make sure to include all new cargo tds into the range
  assignments.forEach(({ loadIndex, dischargeIndex }) => {
    startIndex = Math.min(loadIndex, startIndex);
    endIndex = Math.max(dischargeIndex, endIndex);
  });

  // Expand the range to include full port calls
  [startIndex, endIndex] = expandTdRangeToFullPortCalls(
    timeData,
    startIndex,
    endIndex,
  );

  return {
    timeData,
    startIndex,
    endIndex,
    targetShipmentId,
  };
}

export function getSuggestionTCEDelta(
  originalTimeData: TimeData[],
  targetShipmentId: ClientId | null,
  schedule: NonNullable<BestSchedule>,
  getIncomeForLoadedAmount: GetIncomeForLoadedAmount,
  calculateTimeDataPnl: CalculateTimeDataPnl,
  context: Pick<ScheduleDataContext, 'distances' | 'currentUser'>,
  options: {
    outGoingCoaId: number | null;
    carbonPrice: number | null;
    pnlData: PnlData | null;
    vessel: PnlProcessorContextInput['vessel'];
  },
  /** The range of tds to consider into the TCE delta. overlapping_voyage is a superset of overlapping_indices. */
  range: 'overlapping_indices' | 'overlapping_voyage',
  orderById: Map<number, ScenarioOrder> | null,
) {
  const {
    timeData,
    startIndex,
    endIndex,
    targetShipmentId: finalTargetShipmentId,
  } = calculateTceRange(schedule, targetShipmentId, range);

  const assignments = schedule.combination.assignments;

  const originalFirstChangedIndex = startIndex;
  const originalLastChangedIndex = endIndex - assignments.length * 2;

  const tceOptions: Omit<
    TceComputationOptions,
    'lastIndex' | 'portCallCostSharingDetails'
  > = {
    firstIndex: startIndex,
    euEtsCostEnabled: isEuEtsCostEnabledForUser(context.currentUser),
    fuelEuCostEnabled:
      !!context.currentUser?.rootOrganization?.configIncludeFuelEuInCosts,
    outgoingCoaId: options.outGoingCoaId,
    pnlData: options.pnlData,
  };

  const newPnl = calculateTimeDataPnl(timeData, {
    type: 'vessel',
    vessel: options.vessel,
  });

  // Compute TCE for new schedule
  const newTceResult = computeTceForTimeData(
    timeData,
    finalTargetShipmentId,
    getIncomeForLoadedAmount,
    context.distances,
    {
      ...tceOptions,
      lastIndex: endIndex,
      portCallCostSharingDetails: getCostSharingDetailsForPortCalls(timeData),
    },
    orderById,
    newPnl,
  );

  const originalPnl = calculateTimeDataPnl(originalTimeData, {
    type: 'vessel',
    vessel: options.vessel,
  });

  // Compute TCE for original schedule
  const originalTceResult =
    finalTargetShipmentId &&
    finalTargetShipmentId !== NEW_SUGGESTION_SHIPMENT_ID
      ? computeTceForTimeData(
          originalTimeData,
          finalTargetShipmentId,
          getIncomeForLoadedAmount,
          context.distances,
          {
            ...tceOptions,
            lastIndex: originalTimeData.length - 1,
            portCallCostSharingDetails:
              getCostSharingDetailsForPortCalls(originalTimeData),
          },
          orderById,
          originalPnl,
        )
      : null;

  if (!newTceResult) {
    throw new Error('Failed to compute TCE for new schedule');
  }

  return {
    breakEvenFreight: newTceResult.breakEvenFreight,
    cost: newTceResult.voyageCost,
    totalExtraCosts: newTceResult.totalExtraCosts,
    totalPortCosts: newTceResult.totalPortCost,
    totalChannelCosts: newTceResult.totalChannelCost,
    tcCost: newTceResult.tcCost,
    tce: newTceResult.tce,
    durationInDays: newTceResult.durationInDays,
    seaDurationInDays: newTceResult.seaDurationInDays,
    portActivitiesInDays: newTceResult.portActivitiesInDays,
    suggestionVoyageStart: newTceResult.earliestStart,
    suggestionVoyageEnd: newTceResult.latestEnd,
    income: newTceResult.income,
    missingIncomeOrderIds: newTceResult.missingIncomeOrderIds,
    tceDelta:
      originalTceResult && originalFirstChangedIndex < originalLastChangedIndex
        ? newTceResult.tce - originalTceResult?.tce
        : null,
  };
}

/**
 * TCE of the shipment box that a suggestion produces once accepted. Reuses the box's
 * path (buildAssignedItems): the resulting voyage is reconstructed into a TC
 * EventTimeline from the suggestion's flat timeData (as getTimelines in Timelines.ts),
 * then the target shipment's slice is taken with the box's own primitives — the same
 * getOverlappingEvents slice, the same assignNonOverlappingEventsToNextShipment step for
 * free events that overlap no shipment, the same PnL port costs
 * (computePnlPortCostByPortCall), and the same shipmentId scope. So the tooltip value
 * equals the box by construction rather than by coincidence, including for multi-shipment
 * voyages; Core.voyage-metrics.test.ts pins both the single- and multi-shipment cases.
 */
export function computeSuggestionResultingVoyageTce(
  resultingTimeData: TimeData[],
  /** The merge-target shipment. `null` for a brand-new parcel that forms its own
   * shipment on accept — its orders are scoped under a synthetic shipment id so
   * the slice matches the box that appears post-accept. */
  targetShipmentId: ClientId | null,
  /** Orders added by the suggestion, used to scope a new parcel's own shipment. */
  newOrderIds: Set<number>,
  vessel: Vessel,
  getIncomeForLoadedAmount: GetIncomeForLoadedAmount,
  calculateTimeDataPnl: CalculateTimeDataPnl,
  context: Pick<ScheduleDataContext, 'distances' | 'locations'>,
  options: {
    outGoingCoaId: number | null;
    euEtsCostEnabled: boolean;
    fuelEuCostEnabled: boolean;
    pnlData: PnlData;
    pnlVessel: PnlProcessorContextInput['vessel'];
    currencyRates: GlobalStore['currencyRates'];
    baseCurrency: Currency;
    portCostMarcuraMaps: MarcuraPortCosts;
    portCostPriority: PortCostPriority | undefined;
  },
  orderById: Map<number, ScenarioOrder> | null,
): number | null {
  if (resultingTimeData.length === 0) return null;

  // A new parcel has no shipment yet; accept assigns one. Scope its own orders
  // under a synthetic id so the slice equals the post-accept box.
  const shipmentId = targetShipmentId ?? NEW_SUGGESTION_SHIPMENT_ID;
  const scopedTimeData =
    targetShipmentId === null
      ? patchOrderShipmentIds(resultingTimeData, newOrderIds, shipmentId)
      : resultingTimeData;

  // Reconstruct the rendered TC timeline so port costs and the per-shipment
  // slice come from the same structures the box derives.
  const voyageGroup: VoyageGroup = {
    events: scopedTimeData,
    orders: getOrdersFromTimeDatas(scopedTimeData),
    ...generatePortCallsLegsAndWaits(scopedTimeData, context.locations),
    totals: null,
    inputs: null,
    vessel,
  };
  const timeline: EventTimeline = {
    type: 'tc',
    vesselId: vessel.id,
    vessel,
    id: `vessel-${vessel.id}`,
    title: vessel.name ?? `Unnamed vessel ${vessel.id}`,
    subtitle: null,
    voyageGroups: [voyageGroup],
    isPartial: false,
    estimateIdOrScenario: 'scenario',
  };

  const pnlPortCostByPortCallId = computePnlPortCostByPortCall({
    timeline,
    pnlData: options.pnlData,
    currencyRates: options.currencyRates,
    baseCurrency: options.baseCurrency,
    portCostMarcuraMaps: options.portCostMarcuraMaps,
    portCostPriority: options.portCostPriority,
  });

  // Box per-shipment slice: this shipment's cargo tds plus the voyage group's
  // overlapping free events (port calls, legs, waits), sorted by start.
  const shipmentTds = voyageGroup.events.filter(
    (td) =>
      isCargoEventTimeData(td) && getEventShipmentId(td.event) === shipmentId,
  );
  if (shipmentTds.length === 0) return null;

  const timelineFreeEvents = [
    ...excludeDryDockPeriods(voyageGroup.events).filter(freeEventFilter),
    ...voyageGroup.eventsAndLegs.filter(isLegOrWait),
  ];

  // The box also folds in free events that overlap no shipment window, assigning each
  // to the next shipment (assignNonOverlappingEventsToNextShipment). Replicate that here
  // over the same shipment grouping so the slice — and the TCE — matches the box exactly.
  const sortedShipments = sortBy(
    Object.values(
      groupBy(voyageGroup.events.filter(isCargoEventTimeData), (td) =>
        getEventShipmentId(td.event),
      ),
    ),
    (group) => group[0].start,
  );
  const shipmentIndex = sortedShipments.findIndex(
    (group) => getEventShipmentId(group[0].event) === shipmentId,
  );
  const additionalEvents = assignNonOverlappingEventsToNextShipment(
    sortedShipments.map((group) => ({
      start: group[0].start,
      end: group[group.length - 1].end,
      portCallIds: new Set(
        group.flatMap((td) =>
          td.event.portCallId !== null ? [td.event.portCallId] : [],
        ),
      ),
    })),
    timelineFreeEvents,
  ).get(shipmentIndex);

  const sliceEvents = sortBy(
    [
      ...shipmentTds,
      ...getOverlappingEvents(shipmentTds, timelineFreeEvents, true),
      ...(additionalEvents ?? []),
    ],
    (x) => x.start,
  ).filter(isTimeData);

  const pnl = calculateTimeDataPnl(scopedTimeData, {
    type: 'vessel',
    vessel: options.pnlVessel,
  });

  const tceResult = computeTceForTimeData(
    sliceEvents,
    shipmentId,
    getIncomeForLoadedAmount,
    context.distances,
    {
      euEtsCostEnabled: options.euEtsCostEnabled,
      fuelEuCostEnabled: options.fuelEuCostEnabled,
      outgoingCoaId: options.outGoingCoaId,
      pnlData: options.pnlData,
      portCallCostSharingDetails: getCostSharingDetailsForPortCalls(
        voyageGroup.events,
      ),
      pnlPortCostByPortCallId,
    },
    orderById,
    pnl,
  );

  return tceResult?.tce ?? null;
}

export type VoyagePerformanceMetrics = {
  massDistance: number;
  distance: number;
  idealDistance: number;
  totalMass: number;
  vcp: MathExpr;
  extra?: ExtraVoyageMetrics;
};

export type SuggestionPerformanceMetrics = {
  originalVoyage: VoyagePerformanceMetrics;
  newVoyage: VoyagePerformanceMetrics;
  delta: VoyagePerformanceMetrics;
};

export const computeVoyagePerformanceMetrics = (
  schedule: TimeData[],
  distances: Distances,
  /** Metric tons */
  massCapacity: number,
): VoyagePerformanceMetrics => {
  const massDistance = mathVar(
    countTotalMassDistance(schedule, distances),
    'sum(massDistance)',
  );
  const totalDistance = mathVar(countTotalDistance(schedule), 'sum(distance)');
  const totalMass = sumBy(
    schedule.filter(isLoadEventTimeData),
    (td) => td.mass,
  );

  const idealDistance = mathDiv(massDistance, massCapacity);

  const vcp = mathMul(mathDiv(idealDistance, totalDistance), 100);

  if (+massDistance === 0) {
    return {
      massDistance: 0,
      distance: 0,
      idealDistance: 0,
      totalMass: 0,
      vcp: mathVar(0),
    };
  }

  const extra = computeExtraVoyageMetrics(schedule, distances, massCapacity);

  return {
    massDistance: +massDistance,
    distance: +totalDistance,
    idealDistance: 0,
    totalMass,
    vcp,
    extra,
  };
};

export function computeSuggestionPerformanceMetrics(
  originalSchedule: TimeData[],
  newSchedule: TimeData[],
  distances: Distances,
  /** Metric tons */
  massCapacity: number,
): SuggestionPerformanceMetrics {
  const originalMetrics = computeVoyagePerformanceMetrics(
    originalSchedule,
    distances,
    massCapacity,
  );
  const newMetrics = computeVoyagePerformanceMetrics(
    newSchedule,
    distances,
    massCapacity,
  );
  const orderIdsInOriginal = new Set(
    originalSchedule.filter(isLoadEventTimeData).map((td) => td.event.order.id),
  );
  const newOrders = newSchedule
    .filter(isLoadEventTimeData)
    .filter((td) => !orderIdsInOriginal.has(td.event.order.id))
    .map((td) => td.event.order);
  const directDistanceByOrderId = new Map<number, number>(
    newOrders.map((order) => [
      order.id,
      getOrderShortestDistance(order, distances) ?? 0,
    ]),
  );

  const massDistanceDelta = mathVar(
    newMetrics.massDistance - originalMetrics.massDistance,
    'ΔtotalMassDistance',
  );
  const distanceDelta = mathVar(
    newMetrics.distance - originalMetrics.distance,
    'Δdistance',
  );
  const massDelta = newMetrics.totalMass - originalMetrics.totalMass;
  const massCapacityVar = mathVar(massCapacity, 'massCapacity');
  /** Hypothetical distance to travel if the new orders could fill the vessel to 100% be delivered with perfect efficiency. */
  const idealDistance = mathDiv(massDistanceDelta, massCapacityVar);
  /** Lower bound the vessel will at least have to travel to deliver all new cargo */
  const minimumDistance = mathVar(
    Math.max(...directDistanceByOrderId.values(), +idealDistance),
    'minimumDistance',
  );

  /** Max ratio the vessel could be loaded if all new orders carried at once */
  const maxFillRate = mathVar(
    Math.min(+idealDistance / +minimumDistance, 1),
    'maxFillRate',
  );
  const distanceRatio = mathDiv(idealDistance, distanceDelta);

  const vcp = mathMul(
    +distanceRatio < 1
      ? // Added distance at least the ideal distance, use same scoring as for existing voyages
        distanceRatio
      : // For efficiency above 100%, linear score up to 200% (max when distanceDelta is 0)
        mathMul(maxFillRate, mathSub(2, mathDiv(distanceDelta, idealDistance))),
    100,
  );

  const delta: SuggestionPerformanceMetrics['delta'] = {
    massDistance: +massDistanceDelta,
    distance: +distanceDelta,
    idealDistance: +idealDistance,
    totalMass: massDelta,
    vcp,
  };

  return {
    originalVoyage: originalMetrics,
    newVoyage: newMetrics,
    delta,
  };
}

export type ExtraVoyageMetrics = {
  /** Carried Ton-Miles: Σ w_o · d_o */
  ctm: number;
  /** Demand Ton-Miles: Σ w_o · d_min,o */
  dtm: number;
  /** Available Ton-Miles: DWT × Σ d_k */
  atm: number;
  /** Excess Ton-Miles: CTM − DTM */
  etm: number;
  /** Idle-Capacity Ton-Miles: Σ (DWT − w_k) d_k */
  icw: number;
  /** Total Waste: ATM − DTM (= ETM + ICW) */
  tw: number;
  /** Total Waste Miles: TW / DWT */
  twm: number;
  /** Load Factor: CTM ⁄ ATM */
  loadFactor: number;
};

export function computeExtraVoyageMetrics(
  schedule: TimeData[],
  distances: Distances,
  /** Vessel dead-weight tonnage in metric tons */
  dwt: number,
): ExtraVoyageMetrics {
  const { startTime, endTime } = getEventsFinancialBounds(schedule);
  if (!startTime || !endTime) {
    // When either is null the schedule doesn't contain a full voyage.
    // This can happen especially when we are checking for overlap with an existing voyage, and there isn't any overlap with an existing shipment
    return {
      ctm: 0,
      dtm: 0,
      atm: 0,
      etm: 0,
      icw: 0,
      tw: 0,
      twm: 0,
      loadFactor: 0,
    };
  }
  const events = schedule.filter(
    (td) => td.start >= startTime && td.end <= endTime,
  );
  // Calculate CTM (Carried Ton-Miles) - actual voyage distances
  const ctm = calculateCTM(events);

  // Calculate DTM (Demand Ton-Miles) - shortest routed distances
  const dtm = calculateDTM(events, distances);

  // Calculate ATM (Available Ton-Miles)
  const atm = calculateATM(events, dwt);

  // Calculate ETM (Excess Ton-Miles)
  const etm = ctm - dtm;

  // Calculate ICW (Idle-Capacity Ton-Miles)
  let icw = 0;
  for (const td of events) {
    if (td.mass !== undefined) {
      const unusedCapacity = dwt - td.mass;
      icw += Math.max(0, unusedCapacity) * td.distance;
    }
  }

  // Calculate TW (Total Waste (Ton Miles))
  const tw = atm - dtm;

  // Total Waste Miles
  const twm = tw / dwt;

  // Calculate Load Factor
  const loadFactor = atm > 0 ? ctm / atm : 0;

  return {
    ctm,
    dtm,
    atm,
    etm,
    icw,
    tw,
    twm,
    loadFactor,
  };
}
